import { useState, useRef } from 'react';
import { Mail, FileText, Loader2, Sparkles, Copy, RotateCcw, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { API, TONES } from './cv-data';

export const CoverLetterTab = ({ cv }) => {
  const [form, setForm] = useState({ company_name: '', position: '', job_description: '', tone: 'professionnel' });
  const [letter, setLetter] = useState('');
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const letterRef = useRef(null);

  const canGenerate = form.company_name.trim() && form.position.trim() && cv.personal.firstName.trim();

  const generate = async () => {
    if (!canGenerate) { toast.error('Remplissez le nom, le poste visé et l\'entreprise'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/cover-letter/generate`, {
        first_name: cv.personal.firstName, last_name: cv.personal.lastName,
        email: cv.personal.email, phone: cv.personal.phone,
        city: cv.personal.city, country: cv.personal.country,
        summary: cv.personal.summary, experiences: cv.experiences,
        education: cv.education, skills: cv.skills, languages: cv.languages,
        company_name: form.company_name, position: form.position,
        job_description: form.job_description, tone: form.tone,
      });
      setLetter(data.letter);
      toast.success('Lettre générée !');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la generation');
    } finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    if (!letterRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(letterRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);
      pdf.save(`Lettre_${cv.personal.firstName}_${cv.personal.lastName}_${form.company_name}.pdf`);
      toast.success('Lettre téléchargée !');
    } catch { toast.error('Erreur lors du téléchargement'); }
    finally { setDownloading(false); }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(letter);
    toast.success('Copié dans le presse-papiers');
  };

  return (
    <div className="space-y-6" data-testid="cover-letter-tab">
      {!cv.personal.firstName && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-800" data-testid="letter-info-banner">
          Remplissez d'abord l'onglet <strong>CV</strong> (au minimum vos informations personnelles et votre expérience) pour générer une lettre personnalisée.
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
        <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
            <Mail className="w-4 h-4 text-[#F59E0B]" />
          </div>
          Informations pour la lettre
        </h2>

        {cv.personal.firstName && (
          <div className="bg-[#0D9488]/5 border border-[#0D9488]/15 rounded-xl px-4 py-3 mb-5 text-sm text-[#0F766E]" data-testid="letter-cv-context">
            Candidat : <strong>{cv.personal.firstName} {cv.personal.lastName}</strong>
            {cv.experiences.length > 0 && <> — {cv.experiences.length} expérience(s)</>}
            {cv.skills.length > 0 && <> — {cv.skills.length} compétence(s)</>}
          </div>
        )}

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Entreprise cible <span className="text-red-400">*</span></Label>
              <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Ex: Air Tahiti Nui" className="mt-1" data-testid="letter-company" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Poste vise <span className="text-red-400">*</span></Label>
              <Input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="Ex: Responsable RH" className="mt-1" data-testid="letter-position" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description du poste (optionnel)</Label>
            <textarea value={form.job_description} onChange={e => setForm(f => ({ ...f, job_description: e.target.value }))}
              rows={3} placeholder="Collez ici la description de l'offre d'emploi pour une lettre encore plus ciblee..."
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
              data-testid="letter-job-desc" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Ton de la lettre</Label>
              <Select value={form.tone} onValueChange={v => setForm(f => ({ ...f, tone: v }))}>
                <SelectTrigger className="mt-1" data-testid="letter-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={generating || !canGenerate}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold py-5" data-testid="generate-letter">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération en cours...</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Générer ma lettre</>}
            </Button>
          </div>
        </div>
      </div>

      {letter && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8" data-testid="letter-preview-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#0D9488]/10 flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#0D9488]" />
              </div>
              Votre lettre de motivation
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyToClipboard} className="border-gray-200" data-testid="copy-letter">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier
              </Button>
              <Button variant="outline" size="sm" onClick={generate} disabled={generating} className="border-gray-200" data-testid="regenerate-letter">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Régénérer
              </Button>
              <Button size="sm" onClick={downloadPdf} disabled={downloading}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="download-letter-pdf">
                {downloading ? 'Export...' : <><Download className="w-3.5 h-3.5 mr-1.5" /> PDF</>}
              </Button>
            </div>
          </div>
          <textarea value={letter} onChange={e => setLetter(e.target.value)} rows={20}
            className="w-full rounded-xl border border-gray-200 px-5 py-4 text-sm leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488] mb-4 font-[Georgia,serif] text-gray-700"
            data-testid="letter-editor" />
          <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <div ref={letterRef} style={{ width: '794px', padding: '60px 50px', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: '1.7', color: '#374151', backgroundColor: '#fff' }}>
              <div style={{ textAlign: 'right', marginBottom: '30px', fontSize: '12px', color: '#6B7280' }}>
                {cv.personal.city && <div>{cv.personal.city}{cv.personal.country ? `, ${cv.personal.country}` : ''}</div>}
                <div>{new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 'bold' }}>{cv.personal.firstName} {cv.personal.lastName}</div>
                {cv.personal.email && <div style={{ fontSize: '12px', color: '#6B7280' }}>{cv.personal.email}</div>}
                {cv.personal.phone && <div style={{ fontSize: '12px', color: '#6B7280' }}>{cv.personal.phone}</div>}
              </div>
              {letter.split('\n').map((line, i) => (
                <p key={i} style={{ margin: line.trim() ? '0 0 8px 0' : '0 0 16px 0' }}>{line || '\u00A0'}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
